// eslint-disable-next-line react-refresh/only-export-components -- App is the root; boot screens live here intentionally
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react'
import { Redirect, Route, Switch, useLocation, useRoute } from 'wouter'
import type { Idea, NavTab } from '@/domain/types'
import { useAuthContext } from '@/auth/AuthProvider'
import { useRoles } from '@/auth/useRoles'
import { DevRolePicker } from '@/components/DevRolePicker'
import { SiteHeader } from '@/components/layout/SiteHeader'
import { SiteFooter } from '@/components/layout/SiteFooter'
import { Badge, SubNav } from '@/components/layout/SubNav'
import { Hero } from '@/components/layout/Hero'
import {
  responsiveStyles,
  useIsMobile,
  useVisualViewportVars,
} from '@/components/layout/responsive'
import { MobileHeader, MobileSegments, MobileTabBar } from '@/components/layout/MobileChrome'
import { MobileTitleContext } from '@/components/layout/mobileTitle'
import { NotificationsBell } from '@/components/layout/NotificationsBell'
import { Modal } from '@/components/Modal'
import { RouteErrorBoundary } from '@/components/feedback/RouteErrorBoundary'
import { toggleForcedSkeletons, useForcedSkeletons } from '@/api/skeletonDebug'
import { SkeletonBar } from '@/components/feedback'
import { HomeView } from '@/views/HomeView'
import type { AdminTab } from '@/views/AdminView'
import type { SettingsSection } from '@/views/SettingsView'
import { ADMIN_GROUPS } from '@/views/skeletons/adminShared'
import { SETTINGS_SECTIONS as SETTINGS_ITEMS } from '@/views/skeletons/settingsShared'
import { usePendingCount } from '@/api/hooks/absences'
import { useMySettings } from '@/api/hooks/notifications'
import { useLastData } from '@/api/useLastData'
import { ChatProvider, useChatContext, chatIsActive } from '@/api/foundry/ChatProvider'
import { tokens } from '@/theme'
import { mobileButton, primaryButton, secondaryButton } from '@/components/ui/styles'

// Every route but home is its own chunk, so the first paint does not wait for
// the markdown renderer, the admin panels or the allocation grid. The chunks
// are fetched in the background once the shell is up (see prefetchRoutes), so
// a click normally finds its view already loaded; when it does not, the
// Suspense fallback is that route's own page skeleton (routeSkeleton), the
// same one the view shows while its data loads.
const loaders = {
  ideas: () => import('@/views/IdeasView'),
  ideaDetail: () => import('@/views/IdeaDetailView'),
  initiatives: () => import('@/views/InitiativesView'),
  initiativeDetail: () => import('@/views/InitiativeDetailView'),
  people: () => import('@/views/PeopleView'),
  person: () => import('@/views/PersonView'),
  approvals: () => import('@/views/ApprovalsView'),
  settings: () => import('@/views/SettingsView'),
  bugs: () => import('@/views/BugsView'),
  chat: () => import('@/views/ChatView'),
}
const IdeasView = lazy(() => loaders.ideas().then(m => ({ default: m.IdeasView })))
const IdeaDetailView = lazy(() => loaders.ideaDetail().then(m => ({ default: m.IdeaDetailView })))
const ChatView = lazy(() => loaders.chat().then(m => ({ default: m.ChatView })))
const InitiativesView = lazy(() =>
  loaders.initiatives().then(m => ({ default: m.InitiativesView })),
)
const InitiativeDetailView = lazy(() =>
  loaders.initiativeDetail().then(m => ({ default: m.InitiativeDetailView })),
)
const PeopleView = lazy(() => loaders.people().then(m => ({ default: m.PeopleView })))
const PersonView = lazy(() => loaders.person().then(m => ({ default: m.PersonView })))
const ApprovalsView = lazy(() => loaders.approvals().then(m => ({ default: m.ApprovalsView })))
const SettingsView = lazy(() => loaders.settings().then(m => ({ default: m.SettingsView })))
const BugsView = lazy(() => loaders.bugs().then(m => ({ default: m.BugsView })))
const WelcomeTutorial = lazy(() => import('@/components/tutorials/WelcomeTutorial'))

/**
 * Fetch every route chunk this person can reach, one after another, each in
 * its own idle slot so the page they are looking at keeps the main thread.
 * In the order a person is likely to click; chat last because it is the
 * largest. A chunk that fails here stays failed (the browser memoises the
 * import), so RouteErrorBoundary reloads the page once when such a chunk is
 * navigated to.
 */
function prefetchRoutes(who: { isManager: boolean }): () => void {
  // Someone who asked for less data gets only what they navigate to.
  const connection = (navigator as { connection?: { saveData?: boolean } }).connection
  if (connection?.saveData) return () => undefined

  const order = [
    loaders.ideas,
    loaders.initiatives,
    loaders.people,
    loaders.person,
    loaders.ideaDetail,
    loaders.initiativeDetail,
    ...(who.isManager ? [loaders.approvals] : []),
    loaders.settings,
    loaders.bugs,
    loaders.chat,
  ]
  // Safari has no requestIdleCallback; a short timer is close enough there.
  const hasIdle = typeof window.requestIdleCallback === 'function'
  const idle = (cb: () => void): number =>
    hasIdle ? window.requestIdleCallback(cb) : window.setTimeout(cb, 1000)
  const cancel = (id: number): void =>
    hasIdle ? window.cancelIdleCallback(id) : window.clearTimeout(id)

  // The page skeletons go first and at once: they are what a click shows
  // while a view chunk is still on its way.
  void pageSkeletons().then(
    () => undefined,
    () => undefined,
  )

  let cancelled = false
  let pending: number | null = null
  const whenIdle = () =>
    new Promise<void>(resolve => {
      pending = idle(() => {
        pending = null
        resolve()
      })
    })
  void order.reduce(
    (chain, load) =>
      chain.then(async () => {
        if (cancelled) return
        await whenIdle()
        if (cancelled) return
        await load().then(
          () => undefined,
          () => undefined,
        )
      }),
    Promise.resolve(),
  )
  return () => {
    cancelled = true
    if (pending !== null) cancel(pending)
  }
}

// The page skeletons are one small chunk of their own rather than entry
// bytes for every first paint. It is the first thing prefetchRoutes fetches,
// before any idle wait, so it is there well before a view chunk could be
// missed; the neutral frame covers the few hundred milliseconds before that.
type PageSkeletons = typeof import('@/views/skeletons/pages')
const pageSkeletons = () => import('@/views/skeletons/pages')
const page = <K extends keyof PageSkeletons>(name: K) =>
  lazy(() => pageSkeletons().then(m => ({ default: m[name] })))
const IdeasPageSkeleton = page('IdeasPageSkeleton')
const IdeaDetailPageSkeleton = page('IdeaDetailPageSkeleton')
const BugsPageSkeleton = page('BugsPageSkeleton')
const ApprovalsPageSkeleton = page('ApprovalsPageSkeleton')
const ChatPageSkeleton = page('ChatPageSkeleton')
const PeoplePageSkeleton = page('PeoplePageSkeleton')
const PersonPageSkeleton = page('PersonPageSkeleton')
const InitiativesPageSkeleton = page('InitiativesPageSkeleton')
const InitiativeDetailPageSkeleton = page('InitiativeDetailPageSkeleton')
const SettingsPageSkeleton = page('SettingsPageSkeleton')
// Admin shares its skeleton module with the Settings chunk, so it stays apart.
const AdminPageSkeleton = lazy(() =>
  import('@/views/skeletons/AdminSkeleton').then(m => ({ default: m.AdminPageSkeleton })),
)

interface PageRoute {
  pattern: RegExp
  skeleton: (pathname: string) => ReactNode
  /** A detail page: the phone header goes back to its list and names it until the view loads. */
  detail?: { eyebrow: string; title: string; backTo: string }
}

/**
 * Every tab, its pages and what the shell shows for them, once. The tab is
 * the first entry whose prefix the URL starts with, so home (prefix '') is
 * last. A page is the first pattern that matches, so the more specific one
 * goes first. The <Switch> below stays the router; this is what the chrome
 * and the Suspense fallback read.
 */
const ROUTES: readonly {
  tab: NavTab
  /** Where the phone tab bar and the account sheet send you. */
  path: string
  prefix: string
  /** The phone header's title on the tab's own pages. */
  title: string
  pages: readonly PageRoute[]
}[] = [
  {
    tab: 'chat',
    path: '/chat',
    prefix: '/chat',
    title: 'Chat',
    pages: [{ pattern: /^\/chat$/, skeleton: () => <ChatPageSkeleton /> }],
  },
  {
    tab: 'ideas',
    path: '/ideas',
    prefix: '/ideas',
    title: 'Ideas',
    pages: [
      { pattern: /^\/ideas$/, skeleton: () => <IdeasPageSkeleton /> },
      {
        pattern: /^\/ideas\/[^/]+/,
        skeleton: () => <IdeaDetailPageSkeleton />,
        detail: { eyebrow: 'Idea', title: 'Idea', backTo: '/ideas' },
      },
    ],
  },
  {
    tab: 'initiatives',
    path: '/initiatives',
    prefix: '/initiatives',
    title: 'Initiatives',
    pages: [
      { pattern: /^\/initiatives$/, skeleton: () => <InitiativesPageSkeleton /> },
      {
        pattern: /^\/initiatives\/[^/]+/,
        skeleton: () => <InitiativeDetailPageSkeleton />,
        detail: { eyebrow: 'Initiative', title: 'Initiative', backTo: '/initiatives' },
      },
    ],
  },
  {
    tab: 'people',
    path: '/people',
    prefix: '/people',
    title: 'People',
    pages: [
      { pattern: /^\/people$/, skeleton: () => <PeoplePageSkeleton /> },
      {
        pattern: /^\/people\/[^/]+/,
        skeleton: () => <PersonPageSkeleton crumb="People" me={false} />,
        detail: { eyebrow: 'People', title: 'Person', backTo: '/people' },
      },
    ],
  },
  {
    tab: 'mywork',
    path: '/my/time',
    prefix: '/my/',
    title: 'My work',
    pages: [
      { pattern: /^\/my\/time$/, skeleton: () => <PersonPageSkeleton crumb="My time" me /> },
      { pattern: /^\/my\/approvals$/, skeleton: () => <ApprovalsPageSkeleton /> },
    ],
  },
  {
    tab: 'settings',
    path: '/settings',
    prefix: '/settings',
    title: 'Settings',
    pages: [
      {
        pattern: /^\/settings\/admin/,
        skeleton: pathname => {
          const raw = pathname.split('/')[3]
          return <AdminPageSkeleton tab={ADMIN_TABS.find(t => t === raw)} />
        },
      },
      {
        pattern: /^\/settings/,
        skeleton: pathname => {
          const raw = pathname.split('/')[2]
          return (
            <SettingsPageSkeleton section={SETTINGS_SECTIONS.find(s => s === raw) ?? 'general'} />
          )
        },
      },
    ],
  },
  {
    tab: 'bugs',
    path: '/bugs',
    prefix: '/bugs',
    title: 'Report a bug',
    pages: [{ pattern: /^\/bugs$/, skeleton: () => <BugsPageSkeleton /> }],
  },
  { tab: 'home', path: '/', prefix: '', title: 'Keystone', pages: [] },
]

function tabRoute(pathname: string) {
  // Home's empty prefix matches everything, so there is always one.
  return ROUTES.find(r => pathname.startsWith(r.prefix))!
}

function pageRoute(pathname: string): PageRoute | undefined {
  for (const r of ROUTES) {
    const page = r.pages.find(p => p.pattern.test(pathname))
    if (page) return page
  }
  return undefined
}

/**
 * The Suspense fallback for a route whose chunk is still downloading: the
 * destination page's own skeleton, so the page keeps one shape from the click
 * through the data arriving. Routes without one yet get the neutral frame.
 */
function routeSkeleton(pathname: string): ReactNode {
  const picked = pageRoute(pathname)?.skeleton(pathname) ?? null
  return picked === null ? (
    <RouteLoading />
  ) : (
    <Suspense fallback={<RouteLoading />}>{picked}</Suspense>
  )
}

const globalStyles =
  `
@keyframes skeletonPulse { from { opacity: 0.5; } to { opacity: 1; } }
@media (prefers-reduced-motion: reduce) {
  [data-skeleton] { animation: none !important; opacity: 0.75; }
}
@keyframes barGrow {
  from { transform: scaleX(0); opacity: 0; }
  to   { transform: scaleX(1); opacity: 1; }
}
@keyframes slideUp {
  from { transform: translateY(14px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes weekFromRight {
  from { transform: translateX(12px);  opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
}
@keyframes weekFromLeft {
  from { transform: translateX(-12px); opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  [data-week-step] { animation: none !important; }
}
@keyframes fetchSweep {
  from { transform: translateX(-100%); }
  to   { transform: translateX(340%); }
}
@media (prefers-reduced-motion: reduce) {
  [data-fetch-sweep] { animation: none !important; width: 100%; opacity: 0.6; }
}
` + responsiveStyles

// Chat focus mode — the "Ask Keystone" hero (ChatView) and the copyright footer
// (App shell) collapse when a conversation is active, so the thread fills the
// space between the sticky header and the composer. Injected once in
// AuthenticatedShell so the classes reach both. Grid-rows 1fr→0fr reclaims
// the space with no magic max-height; the inner wrapper clips and slides (hero
// up, footer down). Degrades to an instant collapse where grid-row animation
// isn't supported, and honours prefers-reduced-motion.
const focusModeStyles = `
.keystone-collapse {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows ${tokens.durStd} ${tokens.easeStandard}, opacity ${tokens.durStd} ease;
}
.keystone-collapse > .keystone-collapse__inner {
  min-height: 0;
  overflow: hidden;
  transition: transform ${tokens.durStd} ${tokens.easeStandard};
}
.keystone-collapse.is-collapsed { grid-template-rows: 0fr; opacity: 0; }
.keystone-collapse.is-collapsed > .keystone-collapse__inner { pointer-events: none; }
.keystone-collapse--hero.is-collapsed > .keystone-collapse__inner { transform: translateY(-18px); }
.keystone-collapse--footer.is-collapsed > .keystone-collapse__inner { transform: translateY(18px); }
@media (prefers-reduced-motion: reduce) {
  .keystone-collapse, .keystone-collapse > .keystone-collapse__inner { transition: none; }
}
`

export default function App() {
  const { authState, signIn, signOut } = useAuthContext()

  if (authState.status === 'initialising') {
    return (
      <>
        <style>{globalStyles}</style>
        <BootScreen message="Starting…" />
      </>
    )
  }

  if (authState.status === 'unauthenticated') {
    return (
      <>
        <style>{globalStyles}</style>
        <SignInScreen onSignIn={() => void signIn()} reason={authState.reason} />
      </>
    )
  }

  const { account, roles } = authState

  if (!roles.isReader) {
    return (
      <>
        <style>{globalStyles}</style>
        <NoRoleScreen onSignOut={() => void signOut()} />
      </>
    )
  }

  return (
    <>
      <style>{globalStyles}</style>
      <AuthenticatedShell
        userName={account.name ?? account.username}
        onSignOut={() => void signOut()}
      />
      <DevRolePicker />
    </>
  )
}

// ---------------------------------------------------------------------------
// Authenticated app — route-driven. Active tab is derived from the URL,
// not stored in React state. Detail views read their id from the route
// param via useRoute and pass routed callbacks for back/cross-link.
// ---------------------------------------------------------------------------

interface ShellProps {
  userName: string
  onSignOut: () => void
}

/**
 * The copyright footer, collapsed downward while a chat conversation is active
 * on /chat (chat focus mode). It lives inside ChatProvider — unlike
 * AuthenticatedShell, which *renders* the provider and so can't read chat
 * state itself — so it can key off the conversation. On every other route it
 * renders normally.
 */
function ChatAwareFooter() {
  const chat = useChatContext()
  const [location] = useLocation()
  const collapsed = location === '/chat' && chatIsActive(chat)
  return (
    <div
      className={`keystone-collapse keystone-collapse--footer${collapsed ? ' is-collapsed' : ''}`}
      aria-hidden={collapsed || undefined}
    >
      <div className="keystone-collapse__inner">
        <SiteFooter />
      </div>
    </div>
  )
}

// A non-manager's own requests show their status on My time; Approvals would
// only ever be empty for them.
const MY_WORK_TABS = (isManager: boolean, pendingCount: number) => [
  { id: '/my/time', label: 'My time' },
  ...(isManager ? [{ id: '/my/approvals', label: 'Approvals', badge: pendingCount }] : []),
]

function AuthenticatedShell({ userName, onSignOut }: ShellProps) {
  // Only a Manager decides absence, so only a Manager gets the Approvals tab.
  const { isAdministrator: isAdmin, isManager } = useRoles()
  useEffect(() => prefetchRoutes({ isManager }), [isManager])
  const skeletonsForced = useForcedSkeletons()
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        toggleForcedSkeletons()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  const [location, setLocation] = useLocation()
  const isMobile = useIsMobile()
  const [mobileTitle, setMobileTitle] = useState<string | null>(null)
  const [accountOpen, setAccountOpen] = useState(false)

  const activeTab = deriveActiveTab(location)
  const chrome = mobileChrome(location)
  const backTo = chrome.backTo
  // On a phone the chat is an app screen, not a page: the shell is pinned to
  // the visible area and only the thread scrolls, so opening the keyboard
  // shrinks the thread instead of panning the page under it.
  const chatScreen = isMobile && location === '/chat'
  useVisualViewportVars(chatScreen)

  // The My work badge. Refetched when a decision is made on Approvals, so it
  // does not keep counting a request that is gone, and when you request time
  // off, since a self-managed person decides their own.
  const [pendingKey, setPendingKey] = useState(0)
  const pending = usePendingCount(pendingKey)
  const pendingCount = pending.status === 'success' ? pending.data.count : 0

  // The welcome video pops up until it has been closed once.
  const mySettings = useLastData(useMySettings())
  const welcomeDue = mySettings !== null && !mySettings.tutorialSeen

  // Something done on one page can notify you, so the bell rechecks on every move.
  const [routeKey, setRouteKey] = useState(0)
  const lastLocation = useRef(location)
  useEffect(() => {
    if (lastLocation.current === location) return
    lastLocation.current = location
    setRouteKey(k => k + 1)
  }, [location])

  // A click that changes the route lands at the top of the new page.
  const go = useCallback(
    (path: string) => {
      setLocation(path)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    [setLocation],
  )
  const openIdea = (id: string): void => go(`/ideas/${id}`)
  const openPerson = (userId: string): void => go(`/people/${userId}`)
  const openInitiative = (id: string): void => go(`/initiatives/${id}`)
  const openIdeas = (): void => go('/ideas')

  const bell = <NotificationsBell refreshKey={routeKey} onNavigate={go} compact={isMobile} />

  return (
    // ChatProvider sits above the router so the chat hook (and its
    // in-flight SSE stream, message list, threadId, trusted-tools set)
    // survives navigation between routes. A user can kick off a chat
    // question, navigate to /initiatives to look something up, and
    // come back to /chat to find the answer waiting — no re-asking.
    <ChatProvider>
      <MobileTitleContext.Provider value={setMobileTitle}>
        <div
          style={
            chatScreen
              ? {
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  top: 'var(--keystone-vvtop, 0px)',
                  height: 'var(--keystone-vvh, 100dvh)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : { display: 'flex', flexDirection: 'column', minHeight: '100vh' }
          }
        >
          <style>{focusModeStyles}</style>
          {isMobile ? (
            <MobileHeader
              eyebrow={chrome.eyebrow}
              title={mobileTitle ?? chrome.title}
              onBack={backTo === null ? null : () => setLocation(backTo)}
              onHome={() => go('/')}
              accountActive={
                activeTab === 'mywork' || activeTab === 'settings' || activeTab === 'bugs'
              }
              pendingCount={pendingCount}
              bell={bell}
              onAccount={() => setAccountOpen(true)}
            />
          ) : (
            <DesktopHeader
              active={activeTab}
              go={go}
              userName={userName}
              onSignOut={onSignOut}
              pendingCount={pendingCount}
              bell={bell}
            />
          )}
          {activeTab === 'mywork' &&
            (isMobile ? (
              <MobileSegments
                label="My work"
                segments={MY_WORK_TABS(isManager, pendingCount)}
                active={location}
                onSelect={path => setLocation(path)}
              />
            ) : (
              <SubNav
                title="My work"
                tabs={MY_WORK_TABS(isManager, pendingCount)}
                active={location}
                onSelect={path => setLocation(path)}
              />
            ))}
          {/* Column flex so Chat can push its composer to the bottom of a
            short conversation; every other view is content-height. On a
            phone the bottom padding keeps content clear of the tab bar. */}
          <main
            style={{
              flex: 1,
              minHeight: chatScreen ? 0 : undefined,
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: isMobile ? 'var(--keystone-tabbar-h)' : undefined,
            }}
          >
            <RouteErrorBoundary resetKey={location}>
              <Suspense fallback={routeSkeleton(location)}>
                <Switch>
                  <Route path="/">
                    <HomeRoute onOpenInitiative={openInitiative} onOpenIdeas={openIdeas} />
                  </Route>
                  <Route path="/chat">
                    <ChatView />
                  </Route>
                  {/* Two routes, not one with an inline expansion: a shaper gets
                sent a link to a single idea and has to land on it. */}
                  <Route path="/ideas">
                    <IdeasRoute onOpenIdea={openIdea} onOpenInitiative={openInitiative} />
                  </Route>
                  <Route path="/ideas/:id">
                    <IdeaDetailRoute onOpenInitiative={openInitiative} />
                  </Route>
                  <Route path="/initiatives">
                    <InitiativesRoute onSelectInitiative={openInitiative} onOpenIdeas={openIdeas} />
                  </Route>
                  <Route path="/initiatives/:id">
                    <InitiativeDetailRoute onOpenIdea={openIdea} onOpenPerson={openPerson} />
                  </Route>
                  <Route path="/people">
                    <PeopleView onOpenPerson={openPerson} />
                  </Route>
                  <Route path="/people/:userId">
                    <PersonRoute onOpenInitiative={openInitiative} />
                  </Route>
                  {/* The standalone teams page went with design v2; a team is
                    managed from People now. Old links still land somewhere. */}
                  <Route path="/organisation/teams">
                    <Redirect to="/people" replace />
                  </Route>
                  <Route path="/bugs">
                    <BugsView />
                  </Route>
                  <Route path="/my/time">
                    <PersonView
                      userId="me"
                      crumb="My time"
                      onOpenInitiative={openInitiative}
                      onAbsenceSaved={() => setPendingKey(k => k + 1)}
                    />
                  </Route>
                  {isManager && (
                    <Route path="/my/approvals">
                      <ApprovalsView
                        onOpenPerson={openPerson}
                        onOpenInitiative={openInitiative}
                        onDecided={() => setPendingKey(k => k + 1)}
                      />
                    </Route>
                  )}
                  {isAdmin && (
                    <Route path="/settings/admin/:tab?">
                      <AdminSettingsRoute go={go} onOpenPerson={openPerson} />
                    </Route>
                  )}
                  <Route path="/settings/:section?">
                    <SettingsRoute go={go} onOpenPerson={openPerson} />
                  </Route>
                  {isAdmin && (
                    <Route path="/admin/:tab?">
                      <AdminRedirect />
                    </Route>
                  )}
                  <Route>
                    <NotFoundView onHome={() => setLocation('/')} />
                  </Route>
                </Switch>
              </Suspense>
            </RouteErrorBoundary>
            {skeletonsForced && <SkeletonsForcedBadge />}
          </main>
          {isMobile ? (
            <MobileTabBar active={activeTab} onChange={tab => go(tabToPath(tab))} />
          ) : (
            <ChatAwareFooter />
          )}
          {welcomeDue && (
            <Suspense fallback={null}>
              <WelcomeTutorial />
            </Suspense>
          )}
          {isMobile && (
            <AccountSheet
              open={accountOpen}
              onClose={() => setAccountOpen(false)}
              userName={userName}
              pendingCount={pendingCount}
              onGo={path => {
                setAccountOpen(false)
                go(path)
              }}
              onSignOut={onSignOut}
            />
          )}
        </div>
      </MobileTitleContext.Provider>
    </ChatProvider>
  )
}

/** Inside ChatProvider, so the Chat dropdown's New thread can reset the conversation. */
function DesktopHeader({
  go,
  ...props
}: Omit<ComponentProps<typeof SiteHeader>, 'onNavigate' | 'onNewThread'> & {
  go: (path: string) => void
}) {
  const chat = useChatContext()
  return (
    <SiteHeader
      {...props}
      onNavigate={go}
      onNewThread={starter => {
        if (starter) void chat.sendInNewThread(starter)
        else chat.newThread()
        go('/chat')
      }}
    />
  )
}

function deriveActiveTab(pathname: string): NavTab {
  return tabRoute(pathname).tab
}

/**
 * What the phone header says on each route. Detail routes go back to their
 * list and take their title from the view (useMobileTitle) once it loads.
 */
function mobileChrome(pathname: string): {
  eyebrow: string
  title: string
  backTo: string | null
} {
  const detail = pageRoute(pathname)?.detail
  if (detail) return detail
  const { tab, title } = tabRoute(pathname)
  return { eyebrow: tab === 'home' ? 'TIS initiatives' : 'Keystone', title, backTo: null }
}

/**
 * The phone header has no room for the name and the account's pages, so
 * they sit one tap away behind the account icon.
 */
function AccountSheet({
  open,
  onClose,
  userName,
  pendingCount,
  onGo,
  onSignOut,
}: {
  open: boolean
  onClose: () => void
  userName: string
  pendingCount: number
  onGo: (path: string) => void
  onSignOut: () => void
}) {
  const row: React.CSSProperties = { ...secondaryButton, ...mobileButton, width: '100%', gap: 8 }
  return (
    <Modal open={open} onClose={onClose} title={userName} eyebrow="Signed in">
      <div style={{ display: 'grid', gap: 10 }}>
        <button type="button" onClick={() => onGo('/my/time')} style={row}>
          My work
          {pendingCount > 0 && <Badge n={pendingCount} />}
        </button>
        <button type="button" onClick={() => onGo('/settings')} style={row}>
          Settings
        </button>
        <button type="button" onClick={() => onGo('/bugs')} style={row}>
          Report a bug
        </button>
        <button type="button" onClick={onSignOut} style={row}>
          Sign out
        </button>
      </div>
    </Modal>
  )
}

/** Where a tab goes: the phone tab bar's four, home, and the account sheet's three. */
function tabToPath(tab: NavTab): string {
  return ROUTES.find(r => r.tab === tab)!.path
}

// ---------------------------------------------------------------------------
// Home adapter
//
// Lives inside ChatProvider so the hero's Ask Keystone box can push a question
// straight into the same chat state ChatView renders. Asking from home
// therefore behaves exactly like typing on /chat — the answer streams into
// the shared thread, and the user lands on /chat to watch it arrive.
// ---------------------------------------------------------------------------

function HomeRoute({
  onOpenInitiative,
  onOpenIdeas,
}: {
  onOpenInitiative: (id: string) => void
  onOpenIdeas: () => void
}) {
  const askInChat = useAskInChat()

  return (
    <HomeView onOpenInitiative={onOpenInitiative} onOpenIdeas={onOpenIdeas} onAsk={askInChat} />
  )
}

/**
 * The ideas board. Ideas are created only through chat, which drafts them.
 */
function IdeasRoute({
  onOpenIdea,
  onOpenInitiative,
}: {
  onOpenIdea: (id: string) => void
  onOpenInitiative: (id: string) => void
}) {
  const [, setLocation] = useLocation()
  const shapeInChat = useShapeInChat()

  return (
    <IdeasView
      onOpenIdea={onOpenIdea}
      onOpenInitiative={onOpenInitiative}
      onOpenChat={() => setLocation('/chat')}
      onShapeInChat={shapeInChat}
    />
  )
}

/**
 * Initiatives list. Its empty state sends you to the ideas board rather than
 * into chat: an initiative is promoted from an idea now, so the board is the
 * front door and chat is for asking questions about the portfolio.
 */
function InitiativesRoute({
  onSelectInitiative,
  onOpenIdeas,
}: {
  onSelectInitiative: (id: string) => void
  onOpenIdeas: () => void
}) {
  const [, setLocation] = useLocation()

  return (
    <InitiativesView
      onSelectInitiative={onSelectInitiative}
      onOpenIdeas={onOpenIdeas}
      onOpenChat={() => setLocation('/chat')}
    />
  )
}

/**
 * Push a question into the shared chat thread and route to /chat to watch
 * it stream. Asking from anywhere therefore behaves exactly like typing on
 * /chat — same thread, same audit trail.
 */
function useAskInChat(): (question: string) => void {
  const chat = useChatContext()
  const [, setLocation] = useLocation()

  return useCallback(
    (question: string) => {
      setLocation('/chat')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      void chat.send(question)
    },
    [chat, setLocation],
  )
}

/**
 * Promotion from the board or an idea page. The ideas go to Keystone in a new
 * thread, as a visible message carrying their ids so the agent can read each
 * one; it then shapes the initiative with the shaper and drafts it as a card.
 * A new thread because the agent fixes its stage at the start of one.
 */
function useShapeInChat(): (ideas: Pick<Idea, 'id' | 'title'>[]) => void {
  const chat = useChatContext()
  const [, setLocation] = useLocation()

  return useCallback(
    (ideas: Pick<Idea, 'id' | 'title'>[]) => {
      const lines = ideas.map(i => `- ${i.title} (idea id: ${i.id})`)
      const heading = ideas.length === 1 ? 'this idea' : 'these ideas'
      const message = [`Shape an initiative from ${heading}:`, ...lines].join('\n')
      setLocation('/chat')
      window.scrollTo({ top: 0, behavior: 'smooth' })
      void chat.sendInNewThread(message)
    },
    [chat, setLocation],
  )
}

// ---------------------------------------------------------------------------
// Detail-route adapters
//
// Each adapter parses :id from the route param and validates it. A param that
// is not a GUID lands on the invalid-id screen without calling the API; a
// well-formed id the API rejects with 404 surfaces via the detail view's error
// state.
// ---------------------------------------------------------------------------

function InitiativeDetailRoute({
  onOpenIdea,
  onOpenPerson,
}: {
  onOpenIdea: (id: string) => void
  onOpenPerson: (userId: string) => void
}) {
  const [, params] = useRoute<{ id: string }>('/initiatives/:id')
  const [, setLocation] = useLocation()
  const id = parseRouteId(params?.id)
  if (id === null) {
    return <InvalidRouteParam onBackToList={() => setLocation('/initiatives')} />
  }
  return (
    <InitiativeDetailView
      // Remount on id change: the view holds a refreshKey that several child
      // queries read, and navigating between two initiatives without
      // remounting would show the second with the first's refresh state.
      key={id}
      initiativeId={id}
      onBack={() => setLocation('/initiatives')}
      onOpenIdea={onOpenIdea}
      onOpenPerson={onOpenPerson}
    />
  )
}

/** A person travels by Entra oid, a GUID like every other route id. */
function PersonRoute({ onOpenInitiative }: { onOpenInitiative: (id: string) => void }) {
  const [, params] = useRoute<{ userId: string }>('/people/:userId')
  const [, setLocation] = useLocation()
  const userId = params?.userId ?? ''
  if (!GUID.test(userId)) {
    return <InvalidRouteParam onBackToList={() => setLocation('/people')} />
  }
  return (
    <PersonView
      key={userId}
      userId={userId}
      crumb="People"
      onBack={() => setLocation('/people')}
      onOpenInitiative={onOpenInitiative}
    />
  )
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// The sidebars define the tabs and sections once; the routes follow them.
const ADMIN_TABS: readonly AdminTab[] = ADMIN_GROUPS.flatMap(g => g.items.map(i => i.id))
const SETTINGS_SECTIONS: readonly Exclude<SettingsSection, 'admin'>[] = SETTINGS_ITEMS.map(
  s => s.id,
)

interface SettingsRouteProps {
  go: (path: string) => void
  onOpenPerson: (userId: string) => void
}

/** Each section has its own URL so a link can land on it; `/settings` is General. */
function SettingsRoute({ go, onOpenPerson }: SettingsRouteProps) {
  const [, params] = useRoute<{ section?: string }>('/settings/:section?')
  const { isManager } = useRoles()
  const raw = params?.section ?? 'general'
  const section = SETTINGS_SECTIONS.find(s => s === raw && (s !== 'approval' || isManager))
  if (!section) return <NotFoundView onHome={() => go('/settings')} />
  return (
    <SettingsView
      section={section}
      adminTab="countries"
      onNavigate={go}
      onOpenPerson={onOpenPerson}
    />
  )
}

/** Admin's panels each have a URL too; `/settings/admin` is the first. */
function AdminSettingsRoute({ go, onOpenPerson }: SettingsRouteProps) {
  const [, params] = useRoute<{ tab?: string }>('/settings/admin/:tab?')
  const raw = params?.tab ?? 'countries'
  const tab = ADMIN_TABS.find(t => t === raw)
  if (!tab) return <NotFoundView onHome={() => go('/settings/admin')} />
  return <SettingsView section="admin" adminTab={tab} onNavigate={go} onOpenPerson={onOpenPerson} />
}

/** Admin moved under Settings; links to the old gear's pages still work. */
function AdminRedirect() {
  const [, params] = useRoute<{ tab?: string }>('/admin/:tab?')
  return <Redirect to={`/settings/admin${params?.tab ? `/${params.tab}` : ''}`} replace />
}

function IdeaDetailRoute({ onOpenInitiative }: { onOpenInitiative: (id: string) => void }) {
  const [, params] = useRoute<{ id: string }>('/ideas/:id')
  const [, setLocation] = useLocation()
  const shapeInChat = useShapeInChat()
  const id = parseRouteId(params?.id)
  if (id === null) {
    return <InvalidRouteParam onBackToList={() => setLocation('/ideas')} />
  }
  return (
    <IdeaDetailView
      key={id}
      ideaId={id}
      onBack={() => setLocation('/ideas')}
      onOpenInitiative={onOpenInitiative}
      onShapeInChat={shapeInChat}
    />
  )
}

function parseRouteId(raw: string | undefined): string | null {
  if (!raw || !GUID.test(raw)) return null
  return raw
}

// ---------------------------------------------------------------------------
// Boot / sign-in / no-role / 404 / invalid-id screens
// ---------------------------------------------------------------------------

function BootScreen({ message }: { message: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: tokens.paper,
        fontFamily: tokens.fontDisplay,
        fontSize: 14,
        color: tokens.ink3,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}
    >
      {message}
    </div>
  )
}

/** Shown while a route's chunk downloads: the skeleton views use for a first fetch. */
function RouteLoading() {
  return (
    <div
      className="keystone-pad"
      style={{
        width: '100%',
        maxWidth: tokens.containerMax,
        margin: '0 auto',
        padding: `48px ${tokens.containerPadX}px`,
      }}
    >
      <div aria-busy="true" style={{ display: 'grid', gap: 12 }}>
        <SkeletonBar width={240} height={24} />
        <SkeletonBar width={420} height={13} />
        {[180, 260, 220, 300].map((w, i) => (
          <div key={i} style={{ padding: '14px 0', borderBottom: `1px solid ${tokens.paper3}` }}>
            <SkeletonBar width={w} height={14} delay={i * 80} />
          </div>
        ))}
      </div>
    </div>
  )
}

function SignInScreen({ onSignIn, reason }: { onSignIn: () => void; reason?: string | undefined }) {
  return (
    <>
      <Hero
        eyebrow="Trelleborg Industrial Solutions · Internal"
        headlineTop="Business context,"
        headlineBottom="in one place."
        lede="Keystone collects ideas, shapes them into initiatives and records what came of them. Sign in with your Trelleborg account to continue."
      />
      <div
        className="keystone-pad"
        style={{
          maxWidth: tokens.containerMax,
          margin: '0 auto',
          padding: `48px ${tokens.containerPadX}px`,
        }}
      >
        <button
          onClick={onSignIn}
          // Larger than a form button: it is the only thing on the page to do.
          style={{ ...primaryButton, padding: '12px 28px' }}
        >
          Sign in with Microsoft
        </button>
        {reason && (
          <p
            role="alert"
            style={{
              fontSize: tokens.sizeBody,
              color: tokens.ink2,
              maxWidth: 540,
              lineHeight: 1.65,
              marginTop: 20,
            }}
          >
            The last sign-in was refused: {reason} If you have just joined, ask your Keystone
            administrator to add you to the application, then sign in again.
          </p>
        )}
      </div>
    </>
  )
}

function NoRoleScreen({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div
      className="keystone-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '96px auto',
        padding: `0 ${tokens.containerPadX}px`,
        fontFamily: tokens.fontDisplay,
      }}
    >
      <div
        style={{
          width: 48,
          height: 2,
          background: tokens.gold,
          marginBottom: 24,
        }}
      />
      <h1
        style={{
          fontSize: tokens.sizeH1,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          marginBottom: 16,
        }}
      >
        Access not configured
      </h1>
      <p
        style={{ fontSize: tokens.sizeBodyL, color: tokens.ink2, maxWidth: 540, lineHeight: 1.65 }}
      >
        Your account is authenticated but has not been assigned a Keystone App Role. Ask your
        Keystone administrator to give you a role.
      </p>
      <p
        style={{
          fontSize: tokens.sizeBody,
          color: tokens.ink3,
          maxWidth: 540,
          lineHeight: 1.65,
          marginTop: 16,
        }}
      >
        If you have recently been granted access, sign out and sign back in to refresh your token.
      </p>
      <button onClick={onSignOut} style={{ ...secondaryButton, marginTop: 24 }}>
        Sign out
      </button>
    </div>
  )
}

function NotFoundView({ onHome }: { onHome: () => void }) {
  return (
    <div
      className="keystone-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '96px auto',
        padding: `0 ${tokens.containerPadX}px`,
        fontFamily: tokens.fontDisplay,
        textAlign: 'center',
      }}
    >
      <h1
        style={{
          fontSize: tokens.sizeH1,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          marginBottom: 16,
        }}
      >
        Page not found
      </h1>
      <p style={{ fontSize: tokens.sizeBodyL, color: tokens.ink2, marginBottom: 24 }}>
        The route you tried doesn&rsquo;t exist in Keystone.
      </p>
      <button onClick={onHome} style={primaryButton}>
        Back to home
      </button>
    </div>
  )
}

function InvalidRouteParam({ onBackToList }: { onBackToList: () => void }) {
  return (
    <div
      className="keystone-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '96px auto',
        padding: `0 ${tokens.containerPadX}px`,
        fontFamily: tokens.fontDisplay,
        textAlign: 'center',
      }}
    >
      <h1
        style={{
          fontSize: tokens.sizeH2,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          marginBottom: 16,
        }}
      >
        Invalid identifier
      </h1>
      <p style={{ fontSize: tokens.sizeBody, color: tokens.ink2, marginBottom: 24 }}>
        The id in the URL isn&rsquo;t in the expected format.
      </p>
      <button onClick={onBackToList} style={secondaryButton}>
        Back to list
      </button>
    </div>
  )
}

/** Dev only: says the pages are being held in their loading state, and how to stop. */
function SkeletonsForcedBadge() {
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 50,
        padding: '8px 12px',
        background: tokens.ink,
        color: tokens.paper,
        borderRadius: tokens.radiusMd,
        fontFamily: tokens.fontDisplay,
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      }}
    >
      Skeletons forced · Ctrl+Shift+K to stop
    </div>
  )
}
